import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {PrimaryButton, Stack, Text, TextField, useTheme} from '@fluentui/react';
import {toast} from 'react-toastify';
import PanelWrapper from '../Common/PanelWrapper';
import api, {useGetSupportSessionNotesQuery, useSaveSupportSessionNotesMutation} from '../../store/Api';
import {SUPPORT_NOTES_MAX_LENGTH} from '../../models/ApiKey';

/** How long typing has to pause before the notes save themselves. */
const AUTOSAVE_DELAY_MS = 1000;
const OPEN_STORAGE_KEY = 'ac.supportNotesOpen';

// The mounted panel's save, so ending the session can wait for the last keystrokes to land first.
let pendingFlush: (() => Promise<void>) | undefined;

/** Saves any notes not yet saved. Resolves once they are (or the save has failed); a no-op with nothing pending. */
export const flushSupportSessionNotes = () => pendingFlush?.() ?? Promise.resolve();

/**
 * Whether the notes panel is open. Kept in sessionStorage (per tab, like the session itself) because Layout, and so
 * the banner, remounts on some navigations, and the panel should stay open across them.
 */
export const useSupportNotesOpen = (): [boolean, (open: boolean) => void] => {
    const [open, setOpen] = useState(() => {
        try {
            return window.sessionStorage?.getItem(OPEN_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    });
    const update = useCallback((value: boolean) => {
        setOpen(value);
        try {
            window.sessionStorage?.setItem(OPEN_STORAGE_KEY, value ? '1' : '0');
        } catch {
            // Without sessionStorage the panel just starts closed after a remount.
        }
    }, []);
    return [open, update];
};

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface ISupportSessionNotesPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
}

/**
 * A support session's notes: a docked side panel the person running the session writes in as they work. The notes
 * save themselves as typing pauses, belong to this session only, and appear with it on the organization's Support
 * tab in the partner portal. Once the session ends they can no longer be changed.
 *
 * Stays mounted for the whole session (only the panel opens and closes), so text typed with the panel closed again
 * a moment later is never lost, and the pending save can be flushed when the session is ended.
 */
const SupportSessionNotesPanel: React.FC<ISupportSessionNotesPanelProps> = (props) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const notesQuery = useGetSupportSessionNotesQuery();
    const [saveNotes] = useSaveSupportSessionNotesMutation();
    const [text, setText] = useState<string>();
    const [status, setStatus] = useState<SaveStatus>('idle');
    const [savedAt, setSavedAt] = useState<string>();
    // What the server has, and what is on screen, for the save and the unmount flush (which outlive a render).
    const saved = useRef<string>();
    const latest = useRef<string>();
    const inFlight = useRef<Promise<void>>();
    const timer = useRef<number>();

    // Takes up the stored notes once; after that the text on screen is the source of truth.
    useEffect(() => {
        if (text !== undefined || !notesQuery.data) return;
        saved.current = notesQuery.data.text;
        latest.current = notesQuery.data.text;
        setText(notesQuery.data.text);
        setSavedAt(notesQuery.data.updatedAt);
    }, [notesQuery.data, text]);

    const save = useCallback(async (): Promise<void> => {
        window.clearTimeout(timer.current);
        // One save at a time, so an older text can never land after a newer one.
        if (inFlight.current) await inFlight.current;
        const sending = latest.current;
        if (sending === undefined || sending === saved.current) return;
        setStatus('saving');
        const run = (async () => {
            try {
                const result = await saveNotes({text: sending}).unwrap();
                saved.current = sending;
                setSavedAt(result.updatedAt);
                dispatch(api.util.updateQueryData('getSupportSessionNotes', undefined, () => result));
                setStatus(latest.current === sending ? 'saved' : 'idle');
            } catch {
                setStatus('error');
                throw new Error('Notes not saved');
            }
        })();
        inFlight.current = run.catch(() => undefined).finally(() => { inFlight.current = undefined; });
        await run;
    }, [dispatch, saveNotes]);

    const flush = useCallback(() => save().catch(() => undefined), [save]);

    useEffect(() => {
        pendingFlush = flush;
        return () => {
            if (pendingFlush === flush) pendingFlush = undefined;
            // Unmounting (a remount of Layout, or the session ending) saves whatever is still unsaved.
            if (latest.current !== saved.current) void flush();
        };
    }, [flush]);

    // A tab closed with unsaved notes asks first.
    useEffect(() => {
        const warn = (e: BeforeUnloadEvent) => {
            if (latest.current === saved.current) return;
            void flush();
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [flush]);

    const onChange = (value: string) => {
        setText(value);
        latest.current = value;
        setStatus('idle');
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => void flush(), AUTOSAVE_DELAY_MS);
    };

    const saveNow = async () => {
        const toastId = toast.loading('Saving session notes…');
        try {
            await save();
            toast.update(toastId, {type: 'success', render: 'Session notes saved', isLoading: false, autoClose: 3000});
        } catch {
            toast.update(toastId, {type: 'error', render: 'Failed to save session notes', isLoading: false, autoClose: 4000});
        }
    };

    const length = text?.length ?? 0;
    const statusText = status === 'saving' ? 'Saving…'
        : status === 'error' ? 'Not saved. Typing again or Save retries.'
        : latest.current !== saved.current ? 'Unsaved changes'
        : savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}`
        : '';

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            isBlocking={false}
            customWidthPx={420}
            title="Session notes"
            footerButtons={[
                <PrimaryButton key="save" text="Save" iconProps={{iconName: 'Save'}}
                               disabled={status === 'saving' || text === undefined} onClick={saveNow}/>,
            ]}>
            <Stack tokens={{childrenGap: '0.5em'}} styles={{root: {paddingBottom: '5em'}}}>
                <Text>
                    Saved with this support session as you type, and shown with it on the organization's Support tab in
                    the partner portal. They can't be changed after the session ends.
                </Text>
                {notesQuery.isError && text === undefined ? (
                    <Text styles={{root: {color: theme.palette.redDark}}}>The notes could not be loaded.</Text>
                ) : (
                    <TextField
                        multiline
                        resizable={false}
                        disabled={text === undefined}
                        placeholder={text === undefined ? 'Loading…' : 'What you looked at, what you changed, and why'}
                        value={text ?? ''}
                        maxLength={SUPPORT_NOTES_MAX_LENGTH}
                        onChange={(_, value) => onChange(value ?? '')}
                        styles={{
                            fieldGroup: {height: 'calc(100vh - var(--app-header-offset) - 300px)', minHeight: '10em'},
                            field: {height: '100%'},
                        }}
                    />
                )}
                <Stack horizontal horizontalAlign="space-between">
                    <Text variant="small" styles={{root: {color: status === 'error' ? theme.palette.redDark : theme.palette.neutralSecondary}}}>
                        {statusText}
                    </Text>
                    <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary}}}>
                        {`${length.toLocaleString()} / ${SUPPORT_NOTES_MAX_LENGTH.toLocaleString()}`}
                    </Text>
                </Stack>
            </Stack>
        </PanelWrapper>
    );
};

export default SupportSessionNotesPanel;
